import type {DragEvent, ReactNode} from 'react';
import {RowActionMenu} from '../../shared/RowActionMenu';
import type {RowAction} from '../../shared/DataTableTypes';
import './issue-board.css';

export type IssueBoardColumn = {id:string;label:string;status?:string};

/**
 * One lane of the issue board: the status header with its count and column
 * menu, and the padded body that holds the cards. IssueBoard owns drag state,
 * saving and the move dialog; the design system renders this lane with the
 * real IssueBoardCard (scripts/design-system/component-showcases.tsx).
 */
export function IssueBoardLane<T extends IssueBoardColumn>({column, count, over, actions, empty, onDragOver, onDrop, children}: {
  column: T; count: number;
  /** True while a dragged card may drop here. */
  over?: boolean;
  actions: RowAction<T>[];
  /** Shown after the cards when the lane has none. */
  empty?: string;
  onDragOver?: (event: DragEvent<HTMLElement>) => void;
  onDrop?: (event: DragEvent<HTMLElement>) => void;
  children?: ReactNode;
}) {
  return <section data-column={column.id} className={`issue-board-lane ${over ? 'issue-board-lane--over' : ''}`} aria-label={`${column.label} issues`} onDragOver={onDragOver} onDrop={onDrop}>
    <header className="issue-board-lane-header">
      <span className="issue-board-status-dot" style={{background: column.status ? `var(--color-status-chart-${column.status})` : 'var(--color-primary)'}} aria-hidden="true" />
      <h2 className="issue-board-column-title flex-1" title={column.label}>{column.label}</h2>
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-500 text-[length:var(--oq-font-size-badge)] font-medium leading-none text-white">{count}</span>
      <RowActionMenu item={column} label={`Actions for ${column.label} column`} actions={actions}/>
    </header>
    <div className="issue-board-lane-body">
      {children}
      {empty !== undefined && <div className="issue-board-empty">{empty}</div>}
    </div>
  </section>;
}
